import { useMemo, useState } from "react";
import { ReportDialog } from "@/components/ReportDialog";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Building2, MapPin, Send, Share2, Star, ShieldCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";

import { AppLogo } from "@/components/AppLogo";
import { StatusBadge } from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { sendPublicEnquiry } from "@/lib/public-enquiry.functions";
import { buildShareTargets, copyToClipboard } from "@/lib/share";
import { buildSeo, jsonLdScript, partnerJsonLd, breadcrumbJsonLd, canonicalUrl } from "@/lib/seo";
import type { Purpose } from "@/lib/mock-data";
import {
  fetchPublicPartner,
  PARTNER_SERVICES,
  partnerProfileUrl,
  partnerSeoDescription,
  partnerSeoTitle,
  SERVICE_LABEL,
  type PublicPartnerListing,
  type PublicPartnerProfile,
} from "@/lib/partner-public";

export const Route = createFileRoute("/p/$slug")({
  loader: async ({ params }) => {
    const partner = await fetchPublicPartner(params.slug).catch(() => null);
    if (!partner) throw notFound();
    return { partner };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [{ title: "Partner unavailable | Landoo" }, { name: "robots", content: "noindex" }],
      };
    }
    const p = loaderData.partner;
    const seo = buildSeo({
      title: partnerSeoTitle(p.businessName, p.city),
      description: partnerSeoDescription(p),
      url: partnerProfileUrl(p.businessName, p.userId),
      image: p.logo ?? null,
      imageKind: "partner",
      ogType: "profile",
      nofollow: !p.verified,
    });
    return {
      meta: seo.meta,
      links: seo.links,
      scripts: [
        ...jsonLdScript(
          partnerJsonLd({
            seo,
            businessName: p.businessName,
            city: p.city,
            verified: p.verified,
            averageRating: p.averageRating,
            totalReviews: p.totalReviews,
            activeListings: p.activeListings,
          }),
        ),
        ...jsonLdScript(
          breadcrumbJsonLd([
            { name: "Landoo", url: canonicalUrl("/") },
            { name: p.businessName, url: seo.url },
          ]),
        ),
      ],
    };
  },
  notFoundComponent: () => <Unavailable />,
  errorComponent: () => (
    <Unavailable message="Something went wrong loading this partner profile." />
  ),
  component: PublicPartnerPage,
});

function PublicPartnerPage() {
  const { partner } = Route.useLoaderData();
  return (
    <div className="flex min-h-screen flex-col bg-cream/30">
      <PublicHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 space-y-4 p-4 md:p-6">
        <PartnerView partner={partner} />
      </main>
      <PublicFooter />
    </div>
  );
}

function PublicHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
      <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-4 py-3">
        <Link to="/" className="flex items-center gap-2">
          <AppLogo />
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary"
          >
            Explore map
          </Link>
          <Link
            to="/login"
            className="rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-brown-dark transition hover:brightness-95"
          >
            Sign in
          </Link>
        </div>
      </div>
    </header>
  );
}

function PublicFooter() {
  return (
    <footer className="border-t border-border bg-card px-4 py-6 text-center text-xs text-muted-foreground">
      Partner profile published on Landoo. Contact details stay private until the partner approves a
      request.
    </footer>
  );
}

function PartnerView({ partner }: { partner: PublicPartnerProfile }) {
  const firstWithListings =
    PARTNER_SERVICES.find((s) => partner.listings[s].length > 0) ?? ("sale" as Purpose);
  const [tab, setTab] = useState<Purpose>(firstWithListings);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const rows = partner.listings[tab];
  const allListings = useMemo(
    () => PARTNER_SERVICES.flatMap((s) => partner.listings[s]),
    [partner.listings],
  );

  return (
    <div className="space-y-4">
      {/* Header card */}
      <section className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-brand-soft text-brown-dark">
            {partner.logo ? (
              <img
                loading="lazy"
                decoding="async"
                src={partner.logo}
                alt={`${partner.businessName} logo`}
                className="h-full w-full object-cover"
              />
            ) : (
              <Building2 className="h-8 w-8" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-bold leading-tight text-brown-dark sm:text-2xl">
                {partner.businessName}
              </h1>
              {partner.verified && (
                <span className="inline-flex items-center gap-1 rounded-full bg-rent/15 px-2.5 py-1 text-[11px] font-semibold text-rent">
                  <ShieldCheck className="h-3.5 w-3.5" /> Verified partner
                </span>
              )}
            </div>
            {partner.city && (
              <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0" /> {partner.city}
              </p>
            )}
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-muted-foreground">
              {partner.description ?? "This partner has not added a business description yet."}
            </p>

            <dl className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
              <Stat label="Active listings" value={String(partner.activeListings)} />
              <Stat
                label="Average rating"
                value={
                  partner.totalReviews
                    ? `${partner.averageRating.toFixed(1)} / 5`
                    : "No ratings yet"
                }
                icon={
                  partner.totalReviews ? (
                    <Star className="h-4 w-4 fill-brand text-brand" />
                  ) : undefined
                }
              />
              <Stat label="Total reviews" value={String(partner.totalReviews)} />
            </dl>
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-3">
          <a
            href="#partner-listings"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary sm:text-sm"
          >
            <Building2 className="h-4 w-4" /> View listings
          </a>
          <button
            type="button"
            onClick={() => setEnquiryOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-brand py-2.5 text-xs font-bold text-brown-dark shadow-soft transition hover:brightness-95 sm:text-sm"
          >
            <Send className="h-4 w-4" /> Send enquiry
          </button>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border py-2.5 text-xs font-semibold text-brown-dark transition hover:bg-secondary sm:text-sm"
          >
            <Share2 className="h-4 w-4" /> Share profile
          </button>
        </div>

        <div className="mt-2">
          <ReportDialog
            target={{
              targetType: "partner",
              partnerId: partner.userId,
              partnerName: partner.businessName,
            }}
            label="Report this partner"
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-secondary"
          />
        </div>

        <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
          Phone, WhatsApp and email are hidden in line with this partner's privacy settings. Send an
          enquiry or request contact from a listing page instead.
        </p>
      </section>

      {/* Tabs */}
      <section
        id="partner-listings"
        className="rounded-3xl border border-border bg-card p-4 shadow-soft sm:p-5"
      >
        <div className="flex flex-wrap gap-2">
          {PARTNER_SERVICES.map((s) => {
            const count = partner.listings[s].length;
            const active = tab === s;
            return (
              <button
                key={s}
                type="button"
                onClick={() => setTab(s)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition sm:text-sm ${
                  active
                    ? "bg-brand text-brown-dark shadow-soft"
                    : "border border-border text-muted-foreground hover:bg-secondary"
                }`}
              >
                {SERVICE_LABEL[s]} ({count})
              </button>
            );
          })}
        </div>

        <div className="mt-4">
          {rows.length ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {rows.map((l) => (
                <PublicListingCard key={l.id} listing={l} />
              ))}
            </div>
          ) : (
            <p className="rounded-2xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No active {SERVICE_LABEL[tab].toLowerCase()} listings from this partner right now.
            </p>
          )}
        </div>
      </section>

      {enquiryOpen && (
        <PartnerEnquiryDialog
          partner={partner}
          listings={allListings}
          onClose={() => setEnquiryOpen(false)}
        />
      )}
      {shareOpen && <ShareProfileDialog partner={partner} onClose={() => setShareOpen(false)} />}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-2xl bg-secondary/60 px-3 py-2">
      <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 flex items-center gap-1 text-sm font-semibold text-brown-dark">
        {icon}
        {value}
      </dd>
    </div>
  );
}

function PublicListingCard({ listing }: { listing: PublicPartnerListing }) {
  return (
    <Link
      to="/l/$id"
      params={{ id: listing.id }}
      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-soft transition hover:-translate-y-0.5 hover:shadow-float"
    >
      <div className="relative">
        <img
          src={listing.image}
          alt={listing.title}
          loading="lazy"
          className="h-36 w-full object-cover"
        />
        <div className="absolute left-2 top-2">
          <StatusBadge purpose={listing.service} />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-1 text-sm font-semibold text-brown-dark">{listing.title}</h3>
          <span className="shrink-0 text-sm font-bold text-coral">{listing.priceLabel}</span>
        </div>
        <p className="line-clamp-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" />
          {[listing.address, listing.city].filter(Boolean).join(", ") || "Location on request"}
        </p>
        {listing.propertyType && (
          <span className="mt-1 w-fit rounded-full bg-secondary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brown-dark">
            {listing.propertyType}
          </span>
        )}
      </div>
    </Link>
  );
}

function ShareProfileDialog({
  partner,
  onClose,
}: {
  partner: PublicPartnerProfile;
  onClose: () => void;
}) {
  const url = partnerProfileUrl(partner.businessName, partner.userId);
  const [copied, setCopied] = useState(false);
  const targets = buildShareTargets(url, `${partner.businessName} on Landoo`);

  return (
    <Overlay onClose={onClose} title="Share profile">
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={url}
          className="min-w-0 flex-1 truncate rounded-xl border border-border bg-secondary/50 px-3 py-2 text-xs text-muted-foreground"
        />
        <button
          type="button"
          onClick={async () => {
            const ok = await copyToClipboard(url);
            setCopied(ok);
            toast[ok ? "success" : "error"](ok ? "Profile link copied" : "Could not copy the link");
          }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-brand px-3 py-2 text-xs font-bold text-brown-dark transition hover:brightness-95"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} Copy
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        {targets.map((t) => (
          <a
            key={t.key}
            href={t.href}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl border border-border py-2 text-center text-xs font-semibold text-brown-dark transition hover:bg-secondary"
          >
            {t.label}
          </a>
        ))}
      </div>
    </Overlay>
  );
}

function PartnerEnquiryDialog({
  partner,
  listings,
  onClose,
}: {
  partner: PublicPartnerProfile;
  listings: PublicPartnerListing[];
  onClose: () => void;
}) {
  const [listingId, setListingId] = useState(listings[0]?.id ?? "");
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [message, setMessage] = useState(
    `Hi ${partner.businessName}, I'd like more details about your listings.`,
  );
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = listings.find((l) => l.id === listingId);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await sendPublicEnquiry({
        data: { listingId: selected.id, service: selected.service, name, mobile, message },
      });
      setSent(true);
      toast.success("Enquiry sent to the partner");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the enquiry.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Overlay onClose={onClose} title="Send enquiry">
      {!listings.length ? (
        <p className="text-sm text-muted-foreground">
          This partner has no active listings to enquire about right now.
        </p>
      ) : sent ? (
        <p className="text-sm text-muted-foreground">
          Thanks! Your enquiry has reached {partner.businessName}. They will get back to you on the
          number you shared.
        </p>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs font-semibold text-brown-dark">
            Listing
            <select
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
              className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm font-normal text-brown-dark"
            >
              {listings.map((l) => (
                <option key={l.id} value={l.id}>
                  [{SERVICE_LABEL[l.service]}] {l.title}
                </option>
              ))}
            </select>
          </label>
          <Input
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
          />
          <Input
            placeholder="Mobile number"
            value={mobile}
            onChange={(e) => setMobile(e.target.value)}
            required
            minLength={7}
          />
          <Textarea
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            minLength={5}
          />
          {error && <p className="text-xs font-medium text-destructive">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            className="w-full rounded-xl bg-brand py-2.5 text-sm font-bold text-brown-dark transition hover:brightness-95 disabled:opacity-60"
          >
            {busy ? "Sending…" : "Send enquiry"}
          </button>
        </form>
      )}
    </Overlay>
  );
}

function Overlay({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-brown-dark/40 p-3 sm:items-center">
      <div className="w-full max-w-md rounded-3xl border border-border bg-card p-4 shadow-float">
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-sm font-bold text-brown-dark">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border px-2.5 py-1 text-xs font-semibold text-muted-foreground transition hover:bg-secondary"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Unavailable({ message }: { message?: string }) {
  return (
    <div className="flex min-h-screen flex-col bg-cream/30">
      <PublicHeader />
      <main className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-xl font-bold text-brown-dark">Partner profile unavailable</h1>
        <p className="text-sm text-muted-foreground">
          {message ?? "This partner profile is not public or no longer exists."}
        </p>
        <Link
          to="/"
          className="rounded-full bg-brand px-4 py-2 text-sm font-bold text-brown-dark transition hover:brightness-95"
        >
          Explore the map
        </Link>
      </main>
      <PublicFooter />
    </div>
  );
}
